import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HiOutlineArrowRight,
  HiOutlineBookOpen,
  HiOutlineChatBubbleLeftRight,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi2";

import {
  Button,
  Card,
  Divider,
  PageHeader,
  Spinner,
} from "../../../components/shared";
import { ContactSupportModal } from "../../../components/Support/ContactSupportModal";
import { useSupportArticles, useSupportSubmissions } from "../../../lib/react-query/support";
import { cn } from "../../../lib/cn";
import { toUsDate } from "../../../utils/utils";

const STATUS_LABELS = {
  DRAFT: "Draft",
  NEW: "New",
  IN_REVIEW: "In review",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

const STATUS_STYLES = {
  NEW: "bg-secondary/15 text-primary",
  IN_REVIEW: "bg-amber-100 text-amber-800",
  RESOLVED: "bg-success/10 text-success",
  CLOSED: "bg-surface-hover text-mediumgrey",
  DRAFT: "bg-surface-hover text-mediumgrey",
};

const ACTIONS = [
  {
    kind: "SUPPORT_REQUEST",
    title: "Contact support",
    description: "Tell us what is not working and our team will review it in the Support Inbox.",
    icon: HiOutlineChatBubbleLeftRight,
    action: "Start a request",
  },
  {
    kind: "FEEDBACK",
    title: "Share feedback",
    description: "Tell us what feels clear, confusing, or valuable—or suggest an improvement.",
    icon: HiOutlineQuestionMarkCircle,
    action: "Share feedback",
  },
];

const SupportStatus = ({ status }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold",
      STATUS_STYLES[status] || STATUS_STYLES.DRAFT,
    )}
  >
    {STATUS_LABELS[status] || status || "Submitted"}
  </span>
);

const SupportHub = () => {
  const location = useLocation();
  const [modalKind, setModalKind] = useState(null);
  const { data: articles = [], isLoading: articlesLoading } = useSupportArticles();
  const { data: submissionsData, isLoading: submissionsLoading } = useSupportSubmissions();
  const submissions = submissionsData?.items || [];
  const publishedArticles = Array.isArray(articles) ? articles.slice(0, 3) : [];

  return (
    <div className="min-h-full bg-bggrey">
      <div className="mx-auto max-w-6xl py-2 md:py-4">
        <PageHeader
          breadcrumbs={[{ label: "Home", href: "/dashboard" }, { label: "Help & Support" }]}
          title="Help & Support"
          description="Find an answer or tell us how we can help."
        />

        <Card variant="elevated" radius="xl" className="overflow-hidden">
          <div className="bg-primary px-5 py-7 text-white md:px-8 md:py-9">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary">
                DealNest support
              </p>
              <h2 className="mt-2 text-2xl font-bold md:text-3xl">What do you need today?</h2>
              <p className="mt-2 max-w-xl text-sm leading-6 text-white/75">
                Start with the FAQ for a quick answer, or send a private request with the context
                our team needs to help.
              </p>
            </div>
          </div>

          <div className="grid gap-4 p-5 sm:grid-cols-2 md:p-8">
            {ACTIONS.map(({ kind, title, description, icon: Icon, action }) => (
              <div
                key={kind}
                className="flex flex-col rounded-2xl border border-surface-border bg-white p-5 transition-shadow hover:shadow-md"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-hover text-primary">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-primary">{title}</h3>
                <p className="mt-2 flex-1 text-sm leading-6 text-mediumgrey">{description}</p>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  size="sm"
                  className="mt-5 w-full"
                  onClick={() => setModalKind(kind)}
                >
                  {action}
                </Button>
              </div>
            ))}
          </div>

          <Divider />

          <div className="grid gap-6 p-5 md:grid-cols-[minmax(0,1fr)_280px] md:p-8">
            <section aria-labelledby="faq-heading">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <HiOutlineBookOpen className="h-5 w-5 text-primary" aria-hidden="true" />
                    <h2 id="faq-heading" className="text-lg font-semibold text-primary">
                      Frequently asked questions
                    </h2>
                  </div>
                  <p className="mt-1 text-sm text-mediumgrey">
                    Browse quick answers before opening a request.
                  </p>
                </div>
                <Link
                  to="/faq"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-primary transition hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                >
                  View all FAQs
                  <HiOutlineArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>

              {articlesLoading ? (
                <div className="flex min-h-24 items-center justify-center">
                  <Spinner />
                </div>
              ) : publishedArticles.length ? (
                <div className="mt-5 space-y-2">
                  {publishedArticles.map((article) => (
                    <details key={article.slug} className="group rounded-xl border border-surface-border px-4 py-3">
                      <summary className="cursor-pointer list-none pr-6 text-sm font-semibold text-primary marker:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary">
                        {article.title}
                      </summary>
                      <p className="mt-2 whitespace-pre-line text-sm leading-6 text-mediumgrey">
                        {article.summary || article.body}
                      </p>
                    </details>
                  ))}
                </div>
              ) : (
                <div className="mt-5 rounded-xl border border-dashed border-surface-border bg-surface/50 p-5 text-sm text-mediumgrey">
                  No FAQs have been published yet. You can still contact support for help.
                </div>
              )}
            </section>

            <aside className="rounded-2xl border border-surface-border bg-surface/60 p-5">
              <HiOutlineQuestionMarkCircle className="h-6 w-6 text-primary" aria-hidden="true" />
              <h2 className="mt-3 text-base font-semibold text-primary">What happens next?</h2>
              <p className="mt-2 text-sm leading-6 text-mediumgrey">
                Your request is private and appears in the Support Inbox. A DealNest admin will
                email you only when you opt in to follow-up.
              </p>
              <p className="mt-4 text-xs leading-5 text-mediumgrey">
                Screenshots are optional, page-only, private, and automatically removed after 90
                days.
              </p>
            </aside>
          </div>
        </Card>

        <Card variant="elevated" radius="xl" className="mt-6 overflow-hidden">
          <div className="flex flex-col gap-2 px-5 py-5 md:flex-row md:items-center md:justify-between md:px-8">
            <div>
              <div className="flex items-center gap-2">
                <HiOutlineClock className="h-5 w-5 text-primary" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-primary">Your requests</h2>
              </div>
              <p className="mt-1 text-sm text-mediumgrey">Track the status of requests you have sent.</p>
            </div>
            <span className="text-sm text-mediumgrey">
              {submissions.length} {submissions.length === 1 ? "request" : "requests"}
            </span>
          </div>
          <Divider />
          {submissionsLoading ? (
            <div className="flex min-h-28 items-center justify-center">
              <Spinner />
            </div>
          ) : submissions.length ? (
            <div className="divide-y divide-surface-border">
              {submissions.map((submission) => (
                <div key={submission.public_id} className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between md:px-8">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-primary">
                      {submission.subject || "Support request"}
                    </p>
                    <p className="mt-1 text-xs text-mediumgrey">
                      {submission.reference} · {toUsDate(submission.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <SupportStatus status={submission.status} />
                    {submission.delivery_status === "FAILED" ? (
                      <span className="text-xs text-danger">Delivery needs attention</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center px-5 py-10 text-center">
              <HiOutlineCheckCircle className="h-8 w-8 text-success" aria-hidden="true" />
              <p className="mt-3 text-sm font-semibold text-primary">No requests yet</p>
              <p className="mt-1 text-sm text-mediumgrey">We&apos;ll show your support history here.</p>
            </div>
          )}
        </Card>

        <p className="mt-5 text-center text-xs text-mediumgrey">
          Need a quick answer? <Link className="font-medium text-primary underline" to="/faq">Browse all FAQs</Link>.
        </p>
      </div>

      <ContactSupportModal
        open={Boolean(modalKind)}
        onClose={() => setModalKind(null)}
        kind={modalKind || "SUPPORT_REQUEST"}
        context={{ pagePath: location.pathname }}
      />
    </div>
  );
};

export default SupportHub;
